import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../config';

// Fetch all products for home page
export const fetchAllProducts = createAsyncThunk(
  'products/fetchAllProducts',
  async () => {
    const response = await axios.get(`${API_BASE_URL}/products`, {
      params: { limit: 8, subCategory: '69d0721768659c2e081b4b62' }
    });
    return response.data;
  }
);

// Fetch products by category — single request, correct page, abortable
export const fetchProductsByCategory = createAsyncThunk(
  'products/fetchProductsByCategory',
  async ({ categoryId, page = 1, filters = {} }, { rejectWithValue, signal }) => {
    try {
      const url = new URL(`${API_BASE_URL}/products`);
      url.searchParams.append('category', categoryId);
      url.searchParams.append('page', page);
      url.searchParams.append('limit', '21');
      url.searchParams.append(
        'fields',
        'name,price,priceAfterDiscount,hasDiscount,images,duration,ratingsAverage,ratingsQuantity,category'
      );

      if (filters.price?.gte !== undefined) url.searchParams.append('price[gte]', filters.price.gte);
      if (filters.price?.lte !== undefined) url.searchParams.append('price[lte]', filters.price.lte);
      if (filters.minEyePower !== undefined) url.searchParams.append('minEyePower[gte]', filters.minEyePower);
      if (filters.maxEyePower !== undefined) url.searchParams.append('maxEyePower[lte]', filters.maxEyePower);
      if (filters.subCategory)     url.searchParams.append('subCategory',     filters.subCategory);
      if (filters.duration)        url.searchParams.append('duration',        filters.duration);
      if (filters.colorsAvailable) url.searchParams.append('colorsAvailable', filters.colorsAvailable);
      if (filters.primaryColor)    url.searchParams.append('primaryColor',    filters.primaryColor);
      if (filters.lensesMaterial)  url.searchParams.append('lensesMaterial',  filters.lensesMaterial);
      if (filters.keyword)         url.searchParams.append('keyword',         filters.keyword);
      if (filters.powersAvailable) url.searchParams.append('powersAvailable', filters.powersAvailable);

      // signal cancels the axios request when a newer dispatch supersedes this one
      const { data: res } = await axios.get(url.toString(), { signal });

      return {
        data:             res.data || [],
        paginationResult: res.paginationResult || { currentPage: page, limit: 21, numberOfPages: 1 },
        results:          res.results || 0,
      };
    } catch (error) {
      if (axios.isCancel(error)) return rejectWithValue({ cancelled: true });
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch categories
export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState: {
    items: [],
    allItems: [], // Store all items for client-side pagination
    categories: [],
    status: 'idle',
    categoriesStatus: 'idle',
    error: null,
    currentPage: 1,
    totalPages: 1,
    totalResults: 0,
    paginationResult: null,
    isFiltering: false,
    lastFilters: null,
    isClientPaginated: true // Default to client-side pagination
  },
  reducers: {
    resetProducts: (state) => {
      state.items = [];
      state.status = 'idle';
      state.error = null;
      state.currentPage = 1;
      state.isFiltering = false;
    },
    // Called immediately when the user navigates to a new category
    // so the old products never flash on screen
    clearForCategory: (state) => {
      state.items = [];
      state.status = 'loading';
      state.error = null;
      state.totalResults = 0;
      state.paginationResult = null;
      state.isFiltering = false;
    },
    setIsFiltering: (state, action) => {
      state.isFiltering = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchAllProducts
      .addCase(fetchAllProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.data;
        state.error = null;
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Handle fetchProductsByCategory
      .addCase(fetchProductsByCategory.pending, (state) => {
        // isFiltering=true means a filter changed — keep current products visible (no skeleton)
        // isFiltering=false means a page change or fresh load — show skeleton
        if (!state.isFiltering) state.status = 'loading';
      })
      .addCase(fetchProductsByCategory.fulfilled, (state, action) => {
        state.status      = 'succeeded';
        state.items       = action.payload.data || [];
        state.paginationResult = action.payload.paginationResult;
        state.totalResults     = action.payload.results;
        state.totalPages  = action.payload.paginationResult?.numberOfPages || 1;
        state.currentPage = action.payload.paginationResult?.currentPage || action.meta.arg.page || 1;
        state.error       = null;
        state.isFiltering = false;
      })
      .addCase(fetchProductsByCategory.rejected, (state, action) => {
        // RTK sets meta.aborted=true when .abort() is called on the thunk
        // payload.cancelled=true when axios receives the AbortSignal
        // Both cases = a newer request superseded this one — ignore silently
        if (action.meta.aborted || action.payload?.cancelled) return;
        state.status      = 'failed';
        state.error       = action.payload?.message || action.error.message;
        state.isFiltering = false;
        state.items       = [];
      })
      // Handle fetchCategories
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesStatus = 'loading';
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesStatus = 'succeeded';
        state.categories = action.payload.data;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesStatus = 'failed';
        state.error = action.payload?.message || action.error.message;
      });
  }
});

export const { resetProducts, clearForCategory, setIsFiltering } = productsSlice.actions;
export default productsSlice.reducer; 